import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { createEmptyCircuitDays } from '@/utils/circuits/createEmptyCircuitDays';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';

interface ModalState {
  isOpen: boolean;
  operation: 'add' | 'remove' | 'duplicate';
  messages: string[];
  action: () => void;
}

export const useCircuitDayManagement = ({
  fields,
  insert,
  remove,
  setValue,
  displayedDayIndex,
  setDisplayedDayIndex,
  insertTranslation,
  removeTranslation,
  translations,
}: {
  fields: CircuitFormDataWithTranslations['circuitDays'];
  insert: (
    index: number,
    value: CircuitFormDataWithTranslations['circuitDays'][0],
  ) => void;
  remove: (index: number) => void;
  setValue: UseFormSetValue<CircuitFormDataWithTranslations>;
  displayedDayIndex: number;
  setDisplayedDayIndex: Dispatch<SetStateAction<number>>;
  insertTranslation: (
    index: number,
    getLanguage: (lang: SupportedLocale) => {
      stepTitle?: string;
      description?: string;
    },
  ) => void;
  removeTranslation: (index: number) => void;
  translations?: CircuitFormDataWithTranslations['translations'];
}) => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const handleAddDay = useCallback(
    (index: number) => {
      const messages: string[] = [];
      const hasMultiDayBenefits = fields.some((day, dayIndex) => {
        if (dayIndex <= index) {
          return day.steps.some((step) => {
            if (step.transport?.id && step.transport.nbDays !== undefined) {
              const firstDayIndex = step.transport.indexFirstDay ?? dayIndex;
              return (
                firstDayIndex <= index &&
                index < firstDayIndex + step.transport.nbDays - 1
              );
            }
            if (
              step.accommodations?.id &&
              step.accommodations.nbDays !== undefined
            ) {
              const firstDayIndex =
                step.accommodations.indexFirstDay ?? dayIndex;
              return (
                firstDayIndex <= index &&
                index < firstDayIndex + step.accommodations.nbDays - 1
              );
            }
            return false;
          });
        }
        return false;
      });

      if (hasMultiDayBenefits) {
        messages.push(
          "Attention, vous allez ajouter une étape qui en suit une qui comporte une ou plusieurs prestations qui durent plusieurs jours. Ces prestations vont être rallongées d'un jour.",
        );
      }

      const action = () => {
        const newDay = createEmptyCircuitDays(1)[0];
        newDay.stepTitle = undefined;
        newDay.description = undefined;
        const benefitsToUpdate = new Set<string>();
        const addedBenefits = new Set<string>();
        let currentTransportStepIndex = 0;
        let currentAccommodationStepIndex = 0;

        fields.forEach((day, dayIndex) => {
          if (dayIndex <= index) {
            day.steps.forEach((step) => {
              if (step.transport?.id && step.transport.nbDays !== undefined) {
                const firstDayIndex = step.transport.indexFirstDay ?? dayIndex;
                if (
                  firstDayIndex <= index &&
                  index < firstDayIndex + step.transport.nbDays - 1 &&
                  !addedBenefits.has(step.transport.id)
                ) {
                  while (newDay.steps.length <= currentTransportStepIndex) {
                    newDay.steps.push(createEmptyCircuitDays(1)[0].steps[0]);
                  }
                  newDay.steps[currentTransportStepIndex].transport = {
                    ...step.transport,
                    nbDays: +step.transport.nbDays + 1,
                  };
                  benefitsToUpdate.add(step.transport.id);
                  addedBenefits.add(step.transport.id);
                  currentTransportStepIndex++;
                }
              }

              if (
                step.accommodations?.id &&
                step.accommodations.nbDays !== undefined
              ) {
                const firstDayIndex =
                  step.accommodations.indexFirstDay ?? dayIndex;
                if (
                  firstDayIndex <= index &&
                  index < firstDayIndex + step.accommodations.nbDays - 1 &&
                  !addedBenefits.has(step.accommodations.id)
                ) {
                  while (newDay.steps.length <= currentAccommodationStepIndex) {
                    newDay.steps.push(createEmptyCircuitDays(1)[0].steps[0]);
                  }
                  newDay.steps[currentAccommodationStepIndex].accommodations = {
                    ...step.accommodations,
                    nbDays: +step.accommodations.nbDays + 1,
                  };
                  benefitsToUpdate.add(step.accommodations.id);
                  addedBenefits.add(step.accommodations.id);
                  currentAccommodationStepIndex++;
                }
              }
            });
          }
        });

        fields.forEach((day, dayIndex) => {
          day.steps.forEach((step, stepIndex) => {
            if (
              step.transport?.id &&
              benefitsToUpdate.has(step.transport.id) &&
              step.transport.nbDays !== undefined
            ) {
              setValue(
                `circuitDays.${dayIndex}.steps.${stepIndex}.transport.nbDays`,
                +step.transport.nbDays + 1,
              );
            }
            if (
              step.accommodations?.id &&
              benefitsToUpdate.has(step.accommodations.id) &&
              step.accommodations.nbDays !== undefined
            ) {
              setValue(
                `circuitDays.${dayIndex}.steps.${stepIndex}.accommodations.nbDays`,
                +step.accommodations.nbDays + 1,
              );
            }
          });
        });

        fields.forEach((day, dayIndex) => {
          if (dayIndex > index) {
            day.steps.forEach((step, stepIndex) => {
              if (
                step.transport?.indexFirstDay != undefined &&
                step.transport.indexFirstDay > index
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.transport.indexFirstDay`,
                  +step.transport.indexFirstDay + 1,
                );
              }
              if (
                step.accommodations?.indexFirstDay != undefined &&
                step.accommodations.indexFirstDay > index
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.accommodations.indexFirstDay`,
                  +step.accommodations.indexFirstDay + 1,
                );
              }
              if (
                step.activities?.indexFirstDay != undefined &&
                step.activities.indexFirstDay > index
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.activities.indexFirstDay`,
                  +step.activities.indexFirstDay + 1,
                );
              }
              if (
                step.meals?.indexFirstDay != undefined &&
                step.meals.indexFirstDay > index
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.meals.indexFirstDay`,
                  +step.meals.indexFirstDay + 1,
                );
              }
            });
          }
        });

        insert(index + 1, newDay);
        insertTranslation(index + 1, () => ({
          stepTitle: '',
          description: '',
        }));
        setValue(`circuitDays.${index + 1}.stepTitle`, undefined);
        setValue(`circuitDays.${index + 1}.description`, undefined);
        setValue('nbDays', fields.length + 1);

        if (displayedDayIndex === index + 1) {
          // We cannot move to the newly-created day directly, so stay on the same day for a moment
          setDisplayedDayIndex((prev) => prev + 1);
          setTimeout(() => {
            setDisplayedDayIndex(index + 1);
          }, 0);
        } else {
          setDisplayedDayIndex(index + 1);
        }
      };

      if (messages.length > 0) {
        setModalState({
          isOpen: true,
          operation: 'add',
          messages,
          action,
        });
      } else {
        action();
      }
    },
    [
      fields,
      insert,
      insertTranslation,
      setValue,
      displayedDayIndex,
      setDisplayedDayIndex,
    ],
  );

  const handleRemoveDay = useCallback(
    (index: number) => {
      const messages: string[] = [];
      const benefitsToUpdate = new Set<string>();
      const benefitsToRemove = new Set<string>();
      let hasMultiDayBenefits = false;
      let hasMultiDayBenefitsStartingHere = false;

      fields[index].steps.forEach((step) => {
        if (step.transport?.id && step.transport.nbDays !== undefined) {
          const firstDayIndex = step.transport.indexFirstDay ?? index;
          if (firstDayIndex === index) {
            benefitsToRemove.add(step.transport.id);
            if (step.transport.nbDays > 1) {
              hasMultiDayBenefitsStartingHere = true;
            }
          } else if (firstDayIndex < index) {
            benefitsToUpdate.add(step.transport.id);
            hasMultiDayBenefits = true;
          }
        }
        if (
          step.accommodations?.id &&
          step.accommodations.nbDays !== undefined
        ) {
          const firstDayIndex = step.accommodations.indexFirstDay ?? index;
          if (firstDayIndex === index) {
            benefitsToRemove.add(step.accommodations.id);
            if (step.accommodations.nbDays > 1) {
              hasMultiDayBenefitsStartingHere = true;
            }
          } else if (firstDayIndex < index) {
            benefitsToUpdate.add(step.accommodations.id);
            hasMultiDayBenefits = true;
          }
        }
      });

      if (hasMultiDayBenefits) {
        messages.push(
          "Attention, vous allez supprimer une étape qui comporte une ou plusieurs prestations débutées auparavant et qui durent plusieurs jours. Ces prestations vont être réduites d'un jour.",
        );
      }

      if (hasMultiDayBenefitsStartingHere) {
        if (hasMultiDayBenefits) {
          messages.push(
            'Cette étape comporte également une ou plusieurs prestations qui débutent sur celle-ci. Elles seront supprimées.',
          );
        } else {
          messages.push(
            'Attention, vous allez supprimer une étape qui comporte une ou plusieurs prestations qui durent plusieurs jours. Ces prestations vont être supprimées, y compris sur les jours suivants.',
          );
        }
      }

      const action = () => {
        fields.forEach((day) => {
          day.steps.forEach((step) => {
            if (
              step.transport?.id &&
              benefitsToUpdate.has(step.transport.id) &&
              step.transport.nbDays !== undefined
            ) {
              step.transport.nbDays = +step.transport.nbDays - 1;
            }
            if (
              step.accommodations?.id &&
              benefitsToUpdate.has(step.accommodations.id) &&
              step.accommodations.nbDays !== undefined
            ) {
              step.accommodations.nbDays = +step.accommodations.nbDays - 1;
            }
          });
        });

        fields.forEach((day, dayIndex) => {
          if (dayIndex >= index) {
            day.steps.forEach((step, stepIndex) => {
              if (
                step.transport?.id &&
                benefitsToRemove.has(step.transport.id)
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.transport`,
                  createEmptyCircuitDays(1)[0].steps[0].transport,
                );
              }
              if (
                step.accommodations?.id &&
                benefitsToRemove.has(step.accommodations.id)
              ) {
                setValue(
                  `circuitDays.${dayIndex}.steps.${stepIndex}.accommodations`,
                  createEmptyCircuitDays(1)[0].steps[0].accommodations,
                );
              }
            });
          }
        });

        fields.forEach((day, dayIndex) => {
          if (dayIndex > index) {
            day.steps.forEach((step) => {
              if (
                step.transport?.indexFirstDay != undefined &&
                step.transport.indexFirstDay > index
              ) {
                step.transport.indexFirstDay =
                  +step.transport.indexFirstDay - 1;
              }
              if (
                step.accommodations?.indexFirstDay != undefined &&
                step.accommodations.indexFirstDay > index
              ) {
                step.accommodations.indexFirstDay =
                  +step.accommodations.indexFirstDay - 1;
              }
              if (
                step.activities?.indexFirstDay != undefined &&
                step.activities.indexFirstDay > index
              ) {
                step.activities.indexFirstDay =
                  +step.activities.indexFirstDay - 1;
              }
              if (
                step.meals?.indexFirstDay != undefined &&
                step.meals.indexFirstDay > index
              ) {
                step.meals.indexFirstDay = +step.meals.indexFirstDay - 1;
              }
            });
          }
        });

        remove(index);
        removeTranslation(index);
        setValue('nbDays', fields.length - 1);
        if (displayedDayIndex === 0) {
          setDisplayedDayIndex(-1);
          setTimeout(() => {
            setDisplayedDayIndex(0);
          }, 0);
        } else if (displayedDayIndex >= index) {
          setDisplayedDayIndex((prev) => Math.max(0, prev - 1));
        }
      };

      if (messages.length > 0) {
        setModalState({
          isOpen: true,
          operation: 'remove',
          messages,
          action,
        });
      } else {
        action();
      }
    },
    [
      remove,
      removeTranslation,
      setValue,
      fields,
      displayedDayIndex,
      setDisplayedDayIndex,
    ],
  );

  const handleDuplicateDay = useCallback(
    (index: number) => {
      const messages: string[] = [];
      const dayToDuplicate = fields[index];
      const hasMultiDayBenefits = dayToDuplicate.steps.some((step) => {
        return (
          (step.transport?.nbDays !== undefined && step.transport.nbDays > 1) ||
          (step.accommodations?.nbDays !== undefined &&
            step.accommodations.nbDays > 1)
        );
      });

      const hasAnyPrestations = dayToDuplicate.steps.some((step) => {
        return (
          step.transport?.id ||
          step.accommodations?.id ||
          step.activities?.id ||
          step.meals?.id
        );
      });

      if (hasMultiDayBenefits) {
        messages.push(
          "Attention, vous allez dupliquer une étape comportant certaines prestations qui durent plusieurs jours. Leur durée va être augmentée d'un jour. Les autres prestations vont être doublées.",
        );
      } else if (hasAnyPrestations) {
        messages.push(
          "Attention, vous allez dupliquer une étape comportant une ou plusieurs prestations. Leur durée va être augmentée d'un jour ou les prestations vont être doublées.",
        );
      }

      const action = () => {
        const duplicatedDay = {
          ...dayToDuplicate,
          steps: dayToDuplicate.steps.map((step) => ({ ...step })),
        };

        duplicatedDay.steps.forEach((step, indexStep) => {
          const updatedStep = { ...step };
          if (step.transport?.id) {
            const transportBenefitType =
              step.transport.standardBenefit?.type ??
              step.transport.optionalBenefits?.[0]?.type;
            const isVehicleType =
              transportBenefitType === 'VEHICLE' ||
              transportBenefitType === 'VEHICLE_WITH_CHAUFFEUR';

            if (step.transport.nbDays !== undefined && isVehicleType) {
              const firstDayIndex = step.transport.indexFirstDay ?? index;
              fields.forEach((day) => {
                day.steps.forEach((s) => {
                  if (
                    s.transport?.id === step.transport.id &&
                    s.transport.nbDays !== undefined
                  ) {
                    s.transport.nbDays = +s.transport.nbDays + 1;
                    s.transport.indexFirstDay = firstDayIndex;
                  }
                });
              });
            } else {
              updatedStep.transport = {
                ...step.transport,
                id: createEmptyCircuitDays(1)[0].steps[0].transport.id,
                indexFirstDay: index + 1,
              };
            }
          }

          if (step.accommodations?.id) {
            const firstDayIndex = step.accommodations.indexFirstDay ?? index;
            fields.forEach((day) => {
              day.steps.forEach((s) => {
                if (
                  s.accommodations?.id === step.accommodations.id &&
                  s.accommodations.nbDays !== undefined
                ) {
                  s.accommodations.nbDays = +s.accommodations.nbDays + 1;
                  s.accommodations.indexFirstDay = firstDayIndex;
                }
              });
            });
          }

          if (step.activities?.id) {
            updatedStep.activities = {
              ...step.activities,
              id: createEmptyCircuitDays(1)[0].steps[0].activities.id,
              indexFirstDay: index + 1,
            };
          }
          if (step.meals?.id) {
            updatedStep.meals = {
              ...step.meals,
              id: createEmptyCircuitDays(1)[0].steps[0].meals.id,
              indexFirstDay: index + 1,
            };
          }
          duplicatedDay.steps[indexStep] = updatedStep;
        });

        insert(index + 1, duplicatedDay);
        insertTranslation(index + 1, (lang) => {
          const existingTranslations =
            translations?.[lang]?.circuitDays?.[index];
          return {
            stepTitle: existingTranslations?.stepTitle ?? undefined,
            description: existingTranslations?.description ?? undefined,
          };
        });
        setDisplayedDayIndex(index + 1);

        fields.forEach((day, dayIndex) => {
          if (dayIndex > index) {
            day.steps.forEach((step) => {
              if (
                step.transport?.indexFirstDay != undefined &&
                step.transport.indexFirstDay > index
              ) {
                step.transport.indexFirstDay =
                  +step.transport.indexFirstDay + 1;
              }
              if (
                step.accommodations?.indexFirstDay != undefined &&
                step.accommodations.indexFirstDay > index
              ) {
                step.accommodations.indexFirstDay =
                  +step.accommodations.indexFirstDay + 1;
              }
              if (
                step.activities?.indexFirstDay != undefined &&
                step.activities.indexFirstDay > index
              ) {
                step.activities.indexFirstDay =
                  +step.activities.indexFirstDay + 1;
              }
              if (
                step.meals?.indexFirstDay != undefined &&
                step.meals.indexFirstDay > index
              ) {
                step.meals.indexFirstDay = +step.meals.indexFirstDay + 1;
              }
            });
          }
        });

        setValue('nbDays', fields.length + 1);
      };

      if (messages.length > 0) {
        setModalState({
          isOpen: true,
          operation: 'duplicate',
          messages,
          action,
        });
      } else {
        action();
      }
    },
    [
      fields,
      insert,
      insertTranslation,
      translations,
      setValue,
      setDisplayedDayIndex,
    ],
  );

  return {
    handleAddDay,
    handleRemoveDay,
    handleDuplicateDay,
    modalState,
    closeModal,
  };
};
